import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { TPageResponse, TFilters } from 'redux/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import {
	downloadAvailableReportRequest,
	getReportTypesRequest,
	searchAvailableReportsRequest,
} from 'redux/reducers/reports/reducer';
import {
	getAvailableReports,
	getAvailableReportsLoading,
	getDownloadingReportIds,
	getReportTypes,
} from 'redux/reducers/reports/selectors';
import { AVAILABLE_REPORTS_ORDER_BY } from 'redux/reducers/reports/constants';
import { DOWNLOAD_ACTION, tableStructure as defaultTableStructure } from './constants';
import { TAvailableReportRow } from './types';
import { getReportFileName, mapReportToRow, withReportTypeOptions } from './utils';

export const ReportingCenter: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const list = useSelector(getAvailableReports);
	const listLoading = useSelector(getAvailableReportsLoading);
	const reportTypes = useSelector(getReportTypes);
	const downloadingIds = useSelector(getDownloadingReportIds);

	const tableStructure = useMemo(
		() => withReportTypeOptions(defaultTableStructure, reportTypes),
		[reportTypes],
	);

	useEffect(() => {
		if (!reportTypes) dispatch(getReportTypesRequest());
	}, [dispatch, reportTypes]);

	useEffect(() => {
		const params = queryString.parse(search);
		// The default structure is enough here: the filter options do not affect the request, and
		// using it keeps the list from being requested again once the report types arrive.
		const filters = getFiltersFromNavigateParams(defaultTableStructure, params) as TFilters;

		dispatch(
			searchAvailableReportsRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				filters,
				orderBy: AVAILABLE_REPORTS_ORDER_BY,
			}),
		);
	}, [dispatch, search]);

	const items = useMemo<TPageResponse<TAvailableReportRow> | null>(() => {
		if (!list) return null;
		return { ...list, data: (list.data ?? []).map(mapReportToRow) };
	}, [list]);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action !== DOWNLOAD_ACTION || downloadingIds.includes(id)) return;
			const row = items?.data.find((item) => item.id === id);
			if (!row) return;

			dispatch(downloadAvailableReportRequest({ id, fallbackFileName: getReportFileName(row) }));
		},
		[dispatch, downloadingIds, items],
	);

	const isRowDisabled = useCallback(
		(item: TAvailableReportRow) => downloadingIds.includes(item.id),
		[downloadingIds],
	);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TAvailableReportRow>
				tableStructure={tableStructure}
				items={items}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				disabledRowFn={isRowDisabled}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Finance &amp; VAT / <b>Reporting Center</b>
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
